/**
 * Global Error Handler
 * Prevents browser default error dialogs and handles all uncaught errors gracefully
 * Uses custom dialog manager instead of browser alerts
 */

import { dialogManager, DialogOptions } from "../ui/dialog-manager";

class GlobalErrorHandler {
  private isInitialized = false;

  initialize(): void {
    if (this.isInitialized) return;

    // Handle uncaught JavaScript errors
    window.addEventListener("error", this.handleError.bind(this));

    // Handle unhandled promise rejections
    window.addEventListener(
      "unhandledrejection",
      this.handlePromiseRejection.bind(this)
    );

    // Prevent default browser error dialogs
    this.preventBrowserAlerts();

    this.isInitialized = true;
  }

  private handleError(event: ErrorEvent): void {
    // Prevent the browser from showing default error dialogs
    event.preventDefault();

    // Show custom error dialog only in development
    if (process.env.NODE_ENV === "development") {
      dialogManager.showError(`An error occurred: ${event.message}`, {
        title: "JavaScript Error",
      });
    }
  }

  private handlePromiseRejection(event: PromiseRejectionEvent): void {
    // Prevent the browser from showing default error dialogs
    event.preventDefault();

    // Show custom error dialog only in development
    if (process.env.NODE_ENV === "development") {
      dialogManager.showError(`Promise rejection: ${event.reason}`, {
        title: "Promise Error",
      });
    }
  }

  private preventBrowserAlerts(): void {
    // Override native alert/confirm to ensure they never appear
    // even if called directly by mistake or from third-party code
    const originalAlert = window.alert;
    const originalConfirm = window.confirm;

    window.alert = (message?: string): void => {
      dialogManager.showAlert(String(message || "Alert"));
    };

    window.confirm = (message?: string): boolean => {
      // Since confirm() is synchronous but our dialog is async,
      // we'll show the dialog but return false to prevent blocking
      dialogManager.showConfirm(String(message || "Confirm"));
      return false; // Safe default for automated environments
    };

    // Store original functions in case we need them for debugging
    (
      window as Window & {
        __originalAlert?: typeof originalAlert;
        __originalConfirm?: typeof originalConfirm;
      }
    ).__originalAlert = originalAlert;
    (
      window as Window & {
        __originalAlert?: typeof originalAlert;
        __originalConfirm?: typeof originalConfirm;
      }
    ).__originalConfirm = originalConfirm;
  }

  /**
   * Safe alert that always uses custom dialog
   */
  static async safeAlert(
    message: string,
    options?: Partial<DialogOptions>
  ): Promise<void> {
    try {
      await dialogManager.showAlert(message, options);
    } catch {
      // Last resort: silently fail rather than showing browser alert
      // Error is already logged by the error handler above
    }
  }

  /**
   * Safe confirm that always uses custom dialog
   */
  static async safeConfirm(
    message: string,
    options?: Partial<DialogOptions>
  ): Promise<boolean> {
    try {
      return await dialogManager.showConfirm(message, options);
    } catch {
      // Last resort: return false (safe default)
      return false;
    }
  }
}

// Create and export global instance
export const globalErrorHandler = new GlobalErrorHandler();

// Auto-initialize when module is imported
globalErrorHandler.initialize();
